import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { LearningPath, Track } from '../evaluator/types.ts';
import { loadCatalogo, type Catalogo as Conteudo, type ManifestEntry } from '../content/load.ts';

import { progressoDoPercurso, type Projectos } from '../state/percursos.ts';
import { useEconomia } from '../state/economia.ts';
import { estadosDosProjectos, useProjectos } from '../state/projectos.ts';
import { useCursoActual } from '../state/cursoActual.ts';
import { progressoDoCurso, proximoTopico, seccaoActual, seccoesDoCurso } from '../state/curso.ts';
import { useEventos } from '../state/eventos.ts';
import { useLessonProgress } from '../state/lessonProgress.ts';
import { temAcessoTotal, useIdentidade } from '../state/identidade.ts';
import { demonstracaoDisponivel, useDemonstracao } from '../state/demonstracao.ts';
import { cursoPago, percursoPago, useAcessos } from '../state/acessos.ts';
import { SeloPago } from '../components/SeloPago.tsx';
import { Button, Callout, Card, ProgressBar } from '../components/primitives/index.tsx';
import { EstadoACarregar } from '../components/brand/LoadingMark.tsx';
import { PainelDeProgresso } from './PainelDeProgresso.tsx';
import { ArteDaTrilha } from './ArteDaTrilha.tsx';
import { SiglaDaLinguagem } from './SiglaDaLinguagem.tsx';
import { t } from '../i18n/index.ts';
import './catalogo.css';

/**
 * O ecrã principal: o catálogo de cursos e percursos (ADR 0027, à imagem da
 * referência).
 *
 * Três blocos, por esta ordem: **o teu progresso** — os cursos e percursos
 * que já começaste, com a percentagem e a contagem —, os **percursos**, e os
 * **cursos**. A razão de ser da ordem é a mesma de sempre: a primeira pergunta
 * de quem abre a aplicação é «onde é que eu ia», não «o que existe».
 *
 * As secções de um curso (os módulos) deixaram de estar aqui: vivem na página
 * do curso, que é para onde o cartão leva.
 *
 * Desenha-se **só com o manifesto**. Os pacotes de conteúdo, que são a parte
 * pesada, só se carregam ao entrar numa lição. É por isso que o ecrã abre
 * depressa numa ligação má, que é o caso que interessa (RNF-002).
 */
export function Catalogo() {
  const [catalogo, setCatalogo] = useState<Conteudo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);

  const progressoDeLicoes = useLessonProgress((s) => s.lessons);
  const identidade = useIdentidade((s) => s.identidade);
  // «Chegou de novo» é não ter rasto NENHUM: nem lição tocada, nem ponto, nem
  // minuto estudado, nem sessão. Qualquer um destes faz a pergunta do ecrã
  // mudar de «o que é isto?» para «onde é que eu ia?».
  const temEventos = useEventos((s) => s.eventos.length > 0);
  const temTempo = useEventos((s) => Object.keys(s.tempoPorDia ?? {}).length > 0);
  const concluidas = useMemo(
    () => new Set(Object.entries(progressoDeLicoes).filter(([, e]) => e.completedAt).map(([id]) => id)),
    [progressoDeLicoes],
  );
  // O progresso de um percurso conta o projecto integrador (RN-011, E12) com
  // os mesmos dados locais que a página do percurso usa — senão o cartão
  // dizia «100%» a quem ainda tem o projecto por aprovar.
  const pesoProjecto = useEconomia((s) => s.pesoProjecto);
  const entregas = useProjectos((s) => s.entregas);
  // H-304 — o selo «Pago» nos cartões, a partir da cópia guardada dos acessos.
  const acessos = useAcessos();
  const projectos = useMemo(() => estadosDosProjectos(entregas), [entregas]);
  const cursoActual = useCursoActual((s) => s.trilha);

  useEffect(() => {
    const controlador = new AbortController();
    setErro(null);
    setCatalogo(null);

    (async () => {
      try {
        setCatalogo(await loadCatalogo(controlador.signal));
      } catch (err) {
        if (controlador.signal.aborted) return;
        setErro(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => controlador.abort();
  }, [tentativa]);

  if (erro) {
    return (
      <div className="catalogo">
        <Callout tone="error" role="alert">
          <p>{t('trilhas.erro.titulo')}</p>
          <p>{t('erro.semLigacao')}</p>
        </Callout>
        <Button onClick={() => setTentativa((n) => n + 1)}>{t('trilhas.erro.accao')}</Button>
      </div>
    );
  }

  if (!catalogo) {
    return (
      <div className="catalogo">
        <EstadoACarregar texto={t('trilhas.acarregar')} />
      </div>
    );
  }

  const { trilhas, modulos, percursos } = catalogo;
  const todasAsLicoes = modulos.flatMap((m) => m.lessons);

  // O progresso de cada curso e percurso, uma vez. «Começado» é ter alguma
  // coisa feita ou a meio: um curso por começar não é progresso, e um já
  // concluído continua a ser — quem acabou quer ver o cheio.
  const cursos = trilhas.map((trilha) => ({
    trilha,
    seccoes: seccoesDoCurso(modulos, trilha.id),
    progresso: progressoDoCurso(seccoesDoCurso(modulos, trilha.id), concluidas, progressoDeLicoes),
  }));
  const comecados = cursos.filter((c) => c.progresso.estado !== 'por-comecar');
  const percursosComecados = percursos
    .map((percurso) => ({
      percurso,
      progresso: progressoDoPercurso(percurso, modulos, concluidas, projectos, pesoProjecto),
    }))
    .filter((p) => p.progresso.concluidas > 0);

  // H-220 — a lição exacta onde se continua: no curso onde o estudante estava
  // (o último que abriu), se está a meio; senão no primeiro a meio pela ordem
  // do catálogo. E dentro dele, a lição começada e não terminada antes da
  // primeira por concluir. Só com o manifesto, como tudo neste ecrã.
  const aMeio =
    comecados.find((c) => c.trilha.id === cursoActual && c.progresso.estado === 'em-curso') ??
    comecados.find((c) => c.progresso.estado === 'em-curso');
  const proximo = aMeio ? proximoTopico(aMeio.seccoes, concluidas, progressoDeLicoes) : undefined;

  const chegouDeNovo =
    concluidas.size === 0 && comecados.length === 0 && !temEventos && !temTempo && !identidade;
  // O primeiro curso **com secções**, e não o primeiro do ficheiro: uma
  // trilha planeada à frente deixava o herói sem botão nenhum.
  const primeiroCurso = cursos.find((c) => c.seccoes.length > 0);
  const primeiraLicao = primeiroCurso?.seccoes[0]?.lessons[0];

  return (
    <div className="catalogo entra-suave">
      <InterruptorDeDemonstracao />
      <AvisoDeAcessoTotal />

      {/* À Mimo: quem chega de novo vê uma frase e UM botão primário; quem já
          estudou vê o seu progresso e o Continuar. As duas perguntas são
          diferentes («o que é isto?» / «onde é que eu ia?») e o ecrã responde
          só à que foi feita. */}
      {chegouDeNovo ? (
        <section className="hero">
          <h2 className="hero__titulo">{t('hero.titulo')}</h2>
          <p className="hero__texto">{t('hero.texto')}</p>
          {/* Direito à primeira lição do primeiro curso, sem passar pela página
              do curso: quem chega de novo não tem nada para escolher lá, e
              cada toque a mais antes do primeiro exercício é uma desistência
              (redesenho de 16/08/2026; mantido no ADR 0027). */}
          {primeiraLicao ? (
            <Link
              to="/licao/$lessonId"
              params={{ lessonId: primeiraLicao }}
              className="btn btn--primary btn--block hero__cta"
              aria-label={t('hero.aria', { curso: primeiroCurso!.trilha.title })}
            >
              {t('hero.cta')}
            </Link>
          ) : null}
        </section>
      ) : (
        <PainelDeProgresso licoes={todasAsLicoes} />
      )}

      {aMeio && proximo ? (
        <section className="continuar" aria-label={t('continuar.etiqueta')}>
          <p className="continuar__etiqueta">{t('continuar.etiqueta')}</p>
          <Link
            to="/licao/$lessonId"
            params={{ lessonId: proximo }}
            className="btn btn--primary btn--block"
          >
            {t('continuar.cta', {
              modulo: seccaoActual(aMeio.seccoes, concluidas, progressoDeLicoes)?.title ?? aMeio.trilha.title,
            })}
          </Link>
        </section>
      ) : null}

      <BlocoDaTurma />

      {comecados.length > 0 || percursosComecados.length > 0 ? (
        <Seccao titulo={t('catalogo.progresso')}>
          <ul className="grelha grelha--progresso">
            {comecados.map(({ trilha, progresso }) => (
              <li key={trilha.id}>
                <CartaoDeProgresso
                  etiqueta={t('catalogo.trilhaEtiqueta')}
                  titulo={trilha.title}
                  percentagem={progresso.percentagem}
                  feitas={progresso.feitas}
                  total={progresso.total}
                  rotulo={t('catalogo.curso.aria', {
                    titulo: trilha.title,
                    percentagem: progresso.percentagem,
                    feitas: progresso.feitas,
                    total: progresso.total,
                  })}
                  sigla={trilha.id}
                  pago={cursoPago(acessos, trilha.id)}
                  para={{ to: '/curso/$trackId', params: { trackId: trilha.id } }}
                />
              </li>
            ))}
            {percursosComecados.map(({ percurso, progresso }) => {
              const percentagem =
                progresso.total === 0 ? 0 : Math.floor((progresso.concluidas / progresso.total) * 100);
              return (
                <li key={percurso.id}>
                  <CartaoDeProgresso
                    etiqueta={t('percurso.etiqueta')}
                    titulo={percurso.title}
                    percentagem={percentagem}
                    feitas={progresso.concluidas}
                    total={progresso.total}
                    rotulo={t('catalogo.percurso.aria', {
                      titulo: percurso.title,
                      percentagem,
                      feitas: progresso.concluidas,
                      total: progresso.total,
                    })}
                    pago={percursoPago(acessos, percurso.id)}
                    para={{ to: '/percurso/$pathId', params: { pathId: percurso.id } }}
                  />
                </li>
              );
            })}
          </ul>
        </Seccao>
      ) : null}

      {/* H-140 — os percursos ao lado dos cursos, nunca no lugar deles: a
          secção só existe quando há percursos, e o catálogo de quem só quer
          um curso fica exactamente como estava (ADR 0018 decisão 2). */}
      {percursos.length > 0 ? (
        <Seccao titulo={t('catalogo.percursos')}>
          <ul className="grelha grelha--trilhas">
            {percursos.map((percurso) => (
              <li key={percurso.id}>
                <CartaoDePercurso
                  percurso={percurso}
                  modulos={modulos}
                  concluidas={concluidas}
                  projectos={projectos}
                  pesoProjecto={pesoProjecto}
                  pago={percursoPago(acessos, percurso.id)}
                />
              </li>
            ))}
          </ul>
        </Seccao>
      ) : null}

      <Seccao titulo={t('catalogo.trilhas')}>
        {modulos.length === 0 ? (
          <Callout tone="info">{t('trilhas.vazio')}</Callout>
        ) : (
          <ul className="grelha grelha--trilhas">
            {cursos.map(({ trilha, seccoes }) => (
              <li key={trilha.id}>
                <CartaoDeTrilha trilha={trilha} seccoes={seccoes} pago={cursoPago(acessos, trilha.id)} />
              </li>
            ))}
          </ul>
        )}
      </Seccao>
    </div>
  );
}

interface Turma {
  turma: string;
  estado: string;
  programa: string;
  instituicao: string;
  /** H-156 — o logótipo da instituição, se ela o tiver definido. */
  logotipo?: string | null;
  nivelActual: string | null;
  proximaSessao: { data: string; local: string; tema: string } | null;
  tutores: string[];
}

/**
 * H-223 — o bloco do programa institucional, só para quem está numa turma.
 * Quem não está não vê nada — nem um buraco no lugar dele (ADR 0018
 * decisão 2): sem dados, o componente devolve null e o ecrã fecha como se o
 * bloco nunca tivesse existido. Carrega depois da pintura, como a sonda de
 * sessão: RNF-002 não paga o acompanhamento de ninguém.
 */
function BlocoDaTurma() {
  const identidade = useIdentidade((s) => s.identidade);
  const [turma, setTurma] = useState<Turma | null>(null);

  useEffect(() => {
    if (!identidade) {
      setTurma(null);
      return;
    }
    const controlador = new AbortController();
    fetch('/api/turma', { credentials: 'same-origin', signal: controlador.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((dados: { turma: Turma | null } | null) => setTurma(dados?.turma ?? null))
      .catch(() => {});
    return () => controlador.abort();
  }, [identidade]);

  if (!turma) return null;

  const dataDaSessao = turma.proximaSessao
    ? new Intl.DateTimeFormat('pt-PT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(turma.proximaSessao.data))
    : null;

  return (
    <Card>
      <div className="turma">
        <p className="turma__etiqueta">{t('turma.titulo')}</p>
        <h2 className="turma__nome">
          {turma.logotipo ? (
            // H-156 — a marca da instituição é dado; o `alt` vazio porque o
            // nome vem logo a seguir, e um leitor de ecrã não o quer duas vezes.
            <img className="turma__logotipo" src={turma.logotipo} alt="" width={28} height={28} />
          ) : null}
          {turma.turma} <span className="turma__instituicao">· {turma.instituicao}</span>
        </h2>
        <dl className="turma__dados">
          <dt>{t('turma.programa')}</dt>
          <dd>{turma.programa}</dd>
          {turma.nivelActual ? (
            <>
              <dt>{t('turma.nivel')}</dt>
              <dd>{turma.nivelActual}</dd>
            </>
          ) : null}
          {turma.tutores.length > 0 ? (
            <>
              <dt>{t('turma.tutores')}</dt>
              <dd>{turma.tutores.join(', ')}</dd>
            </>
          ) : null}
          {turma.proximaSessao && dataDaSessao ? (
            <>
              <dt>{t('turma.proximaSessao')}</dt>
              <dd>
                {t('turma.sessao', {
                  data: dataDaSessao,
                  local: turma.proximaSessao.local,
                  tema: turma.proximaSessao.tema,
                })}
              </dd>
            </>
          ) : null}
        </dl>
      </div>
    </Card>
  );
}

/**
 * O interruptor do modo de demonstração. Não existe no pacote de produção:
 * `demonstracaoDisponivel` é uma constante que o empacotador resolve, e este
 * componente inteiro desaparece com ela. Ver `state/demonstracao.ts`.
 *
 * Desligado, é um botão discreto com uma frase que diz para que serve — um
 * botão que desbloqueia tudo e não se explica seria pior do que não existir.
 * Ligado, é o aviso permanente, com a forma de o desligar ao lado.
 */
function InterruptorDeDemonstracao() {
  const activo = useDemonstracao((s) => s.activo);
  const alternar = useDemonstracao((s) => s.alternar);
  if (!demonstracaoDisponivel) return null;

  if (activo) {
    return (
      <Callout tone="info" role="status">
        <div className="demo__linha">
          <p>{t('demo.activo')}</p>
          <Button variant="secondary" onClick={alternar}>
            {t('demo.desligar')}
          </Button>
        </div>
      </Callout>
    );
  }

  return (
    <div className="demo__linha demo__linha--desligado">
      <p className="demo__explicacao">{t('demo.explicacao')}</p>
      <Button variant="ghost" onClick={alternar}>
        {t('demo.ligar')}
      </Button>
    </div>
  );
}

/**
 * O aviso da conta de acesso total — o irmão do interruptor acima, e existe
 * pela mesma razão: **um modo especial silencioso acaba sempre com alguém a
 * reportar como defeito o que é a sua própria configuração.** Quem abrisse
 * esta conta veria o caminho todo destrancado e concluiria que a sequência de
 * pré-requisitos está avariada.
 *
 * O que muda em relação ao interruptor é que não há botão para desligar: isto
 * não é uma preferência deste navegador, é uma propriedade da conta, e retira-
 * se apagando a linha em `acesso_total`. Um botão que prometesse desligá-lo
 * teria de mentir ou de escrever na base de dados, e nenhuma das duas serve.
 *
 * Ao contrário do interruptor, **este existe no pacote de produção** — é essa
 * a peça que faltava. Vale uma frase e uma condição, e não o código do botão.
 */
function AvisoDeAcessoTotal() {
  const acessoTotal = useIdentidade(temAcessoTotal);
  if (!acessoTotal) return null;

  return (
    <Callout tone="info" role="status">
      <p>{t('acessoTotal.activo')}</p>
      <p>{t('acessoTotal.naoEPagamento')}</p>
    </Callout>
  );
}

function Seccao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  const id = `seccao-${titulo.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <section className="seccao" aria-labelledby={id}>
      <h2 className="seccao__titulo" id={id}>
        {titulo}
      </h2>
      {children}
    </section>
  );
}

/**
 * ADR 0027 — o cartão de «O teu progresso»: etiqueta, título, a percentagem
 * e a contagem, à referência. A percentagem mede-se em lições e a contagem
 * em secções (ou lições, num percurso); as duas dizem coisas diferentes e
 * são ambas úteis — «21% · 3/14» é «um quinto feito, três secções fechadas».
 * O rótulo diz tudo por extenso.
 */
function CartaoDeProgresso({
  etiqueta,
  titulo,
  percentagem,
  feitas,
  total,
  rotulo,
  sigla,
  pago,
  para,
}: {
  etiqueta: string;
  titulo: string;
  percentagem: number;
  feitas: number;
  total: number;
  rotulo: string;
  sigla?: string;
  pago?: boolean;
  para:
    | { to: '/curso/$trackId'; params: { trackId: string } }
    | { to: '/percurso/$pathId'; params: { pathId: string } };
}) {
  const corpo = (
    <div className="cartao__corpo cartao__corpo--progresso">
      <div className="cartao__cabeca">
        <p className="cartao__etiqueta">
          {etiqueta}
          {pago ? <SeloPago /> : sigla ? <SiglaDaLinguagem trilha={sigla} /> : null}
        </p>
        <h3 className="cartao__titulo">{titulo}</h3>
        <p className="cartao__linha">
          <span className="contagem">{t('catalogo.contagem', { feitas, total })}</span>
        </p>
      </div>
      {/* O anel de progresso (07/09/2026, à imagem do KooBits): a percentagem
          num círculo que se enche. Decorativo — o rótulo da ligação leva os
          dois números por extenso, por isso vai `aria-hidden`. */}
      <div className="anel" style={{ ['--pct']: percentagem } as React.CSSProperties} aria-hidden="true">
        <span className="anel__valor">{t('catalogo.anel', { n: percentagem })}</span>
      </div>
    </div>
  );

  return (
    <article className={percentagem === 100 ? 'cartao cartao--completo' : 'cartao'}>
      {para.to === '/curso/$trackId' ? (
        <Link
          to={para.to}
          params={para.params}
          search={{}}
          className="cartao__interior cartao__interior--ligacao"
          aria-label={rotulo}
        >
          {corpo}
        </Link>
      ) : (
        <Link
          to={para.to}
          params={para.params}
          className="cartao__interior cartao__interior--ligacao"
          aria-label={rotulo}
        >
          {corpo}
        </Link>
      )}
    </article>
  );
}

/**
 * Cartão de curso: a arte, a etiqueta, o título, a descrição e o nível (ADR
 * 0015, decisão 5). Uma trilha sem módulos não é uma ligação: continua
 * visível, porque a sequência do produto é informação útil, mas não finge que
 * se pode abrir. Um cartão que parece accionável e não faz nada lê-se como
 * avaria. O estado só se escreve quando não é «disponível»: «em construção»
 * rotula, não esconde.
 */
function CartaoDeTrilha({ trilha, seccoes, pago = false }: { trilha: Track; seccoes: ManifestEntry[]; pago?: boolean }) {
  const topicos = seccoes.reduce((total, m) => total + m.lessons.length, 0);
  const vazia = seccoes.length === 0;
  const nivel = t(`trilha.nivel.${trilha.nivel}`);

  const corpo = (
    <>
      <ArteDaTrilha trilha={trilha.id} />
      <div className="cartao__corpo">
        <p className="cartao__etiqueta">
          {t('catalogo.trilhaEtiqueta')}
          <span className="cartao__etiqueta-lado">
            {pago ? <SeloPago /> : null}
            <SiglaDaLinguagem trilha={trilha.id} />
          </span>
        </p>
        <h3 className="cartao__titulo">{trilha.title}</h3>
        <p className="cartao__descricao">{trilha.description}</p>
        <p className={vazia ? 'cartao__estado cartao__estado--espera' : 'cartao__estado'}>
          <span aria-hidden="true" className="cartao__pastilha" />
          {nivel}
          {trilha.estado !== 'publicada' ? (
            <span className="cartao__estado-extra">· {t(`trilha.estado.${trilha.estado}`)}</span>
          ) : null}
        </p>
      </div>
    </>
  );

  if (vazia) {
    return (
      <article className="cartao cartao--trilha cartao--espera">
        <div className="cartao__interior" aria-label={t('trilha.aria.espera', { titulo: trilha.title })}>
          {corpo}
        </div>
      </article>
    );
  }

  return (
    <article className="cartao cartao--trilha">
      {/* A ligação envolve o cartão inteiro: um alvo do tamanho do cartão é
          muito mais fácil de acertar com o polegar do que só o título. */}
      <Link
        to="/curso/$trackId"
        params={{ trackId: trilha.id }}
        search={{}}
        className="cartao__interior cartao__interior--ligacao"
        aria-label={t('trilha.aria.curso', {
          titulo: trilha.title,
          n: seccoes.length,
          licoes: topicos,
          nivel,
        })}
      >
        {corpo}
      </Link>
    </article>
  );
}

/**
 * H-140/H-142 — o cartão de percurso. A etiqueta diz «Percurso» porque a de
 * trilha diz a linguagem: as duas coisas partilham a grelha e a diferença tem
 * de se ler no cartão, não na secção. Os números vão no `aria-label`; a barra
 * só aparece a quem já fez alguma coisa — o progresso por extenso vive no
 * bloco «O teu progresso».
 */
function CartaoDePercurso({
  percurso,
  modulos,
  concluidas,
  projectos,
  pesoProjecto,
  pago = false,
}: {
  percurso: LearningPath;
  modulos: ManifestEntry[];
  concluidas: ReadonlySet<string>;
  projectos: Projectos;
  pesoProjecto: number;
  pago?: boolean;
}) {
  const progresso = progressoDoPercurso(percurso, modulos, concluidas, projectos, pesoProjecto);

  return (
    <article className="cartao cartao--trilha cartao--percurso">
      <Link
        to="/percurso/$pathId"
        params={{ pathId: percurso.id }}
        className="cartao__interior cartao__interior--ligacao"
        aria-label={t('percurso.aria.abrir', {
          titulo: percurso.title,
          n: percurso.levels.length,
          feitas: progresso.concluidas,
          total: progresso.total,
        })}
      >
        <div className="cartao__corpo">
          <p className="cartao__etiqueta">
            {t('percurso.etiqueta')}
            {pago ? <SeloPago /> : null}
          </p>
          <h3 className="cartao__titulo">{percurso.title}</h3>
          <p className="cartao__descricao">{percurso.description}</p>
          <p className="cartao__estado">
            <span aria-hidden="true" className="cartao__pastilha" />
            {percurso.estado === 'publicada'
              ? t('percurso.niveis', { n: percurso.levels.length })
              : t(`trilha.estado.${percurso.estado}`)}
          </p>
          {progresso.concluidas > 0 ? (
            <div className="cartao__progresso">
              <ProgressBar value={progresso.concluidas} max={Math.max(progresso.total, 1)} />
            </div>
          ) : null}
        </div>
      </Link>
    </article>
  );
}
